
import React from "react";
import { Row, Col, Card } from "antd";
import { useNavigate } from "react-router-dom";
import {
  BarChart2,
  FileText,
  Users,
  Activity,
  DollarSign,
  Folder,
  User,
  TrendingUp,
  PieChart,
  FileText as Receipt, 
  File as Rocket,
} from "react-feather";
import { FileOutlined, BarChartOutlined, UserOutlined, AccountBookOutlined as Beds } from "@ant-design/icons";
const categoryColors = {
  "Flash Reports": "#007bff",
  "Reservation & Occupancy Reports": "#fd7e14",
  "Financial Reports": "#ffc107",
  "Operational & Guest Insights": "#dc3545",
  "OTA Reports": "#6610f2",
};

const frontOfficeReports = [
  {
    category: "Flash Reports",
    reports: [
      { icon: <BarChart2 />, title: "Daily Flash Report", description: "Quick snapshot of the day's revenue, occupancy, and financials.", link: "/hospitality/daily-flash-report" },
      { icon: <Activity />, title: "Shift End Summary", description: "Summary of front office activities at shift closure.", link: "/hospitality/shift-end-summary" },
      { icon: <TrendingUp />, title: "Sales Performance and Occupancy", description: "Comprehensive sales performance and occupancy analysis.", link: "/hospitality/sales-performance" },
    ],
  },
  {
    category: "Reservation & Occupancy Reports",
    reports: [
      { icon: <Users />, title: "Reservation Report", description: "Track confirmed, canceled, and tentative reservations.", link: "/hospitality/reservation-report" },
      { icon: <User />, title: "Occupancy Report", description: "Overview of occupied vs available rooms for any period.", link: "/hospitality/occupancy-report" },
      { icon: <FileText />, title: "Room Status Report", description: "Tracks vacant, occupied, and maintenance rooms.", link: "/hospitality/room-status-report" },
    ],
  },
  {
    category: "Financial Reports",
    reports: [
      { icon: <DollarSign />, title: "Daily Revenue Report", description: "Breakdown of daily earnings from rooms and services.", link: "/hospitality/daily-revenue-analysis" },
      { icon: <TrendingUp />, title: "Payment Mode Report", description: "Cash, card, and UPI transactions summary.", link: "/hospitality/payment-mode-report" },
      { icon: <Activity />, title: "Refund & Adjustments", description: "Records of refunds and adjustments processed.", link: "/reports/refunds" },
      { icon: <Folder />, title: "Tax Compliance Report", description: "Overview of collected taxes, GST, and VAT.", link: "/reports/tax-compliance" },
      { icon: <BarChart2 />, title: "Room Wise Sales Report", description: "Detailed revenue report for each room category.", link: "/hospitality/roomno-wise-sales" },
    ],
  },
  {
    category: "Operational & Guest Insights",
    reports: [
      { icon: <Users />, title: "Guest Database", description: "Comprehensive guest information and history.", link: "/hospitality/guest-database" },
      { icon: <FileText />, title: "Room Sold Reports", description: "Details of rooms sold with pricing and tax breakdowns.", link: "/hospitality/daily-room-sold" },

        { icon: <Receipt />, title: "Check Out / Guest Billing",  description: "Details of Guest check out Billing summary with tax breakdown.",link: "/hospitality/front-office-billings", category: "Flash Reports" },

    ],
  },
  {
    category: "OTA Reports",
    reports: [
      { icon: <PieChart />, title: "OTA Booking Volume Report", description: "Breakdown of bookings from different OTA platforms.", link: "/hospitality/ota-booking-volume" },
      { icon: <TrendingUp />, title: "OTA Revenue Performance", description: "Tracks revenue generated from OTA partners.", link: "/hospitality/ota-booking-revenue" },
      { icon: <Users />, title: "OTA Customer Insights", description: "Demographics and behavior of OTA guests.", link: "/reports/ota-customer-insights" },
    ],
  },
];

const quickLinks = [
  { icon: <BarChart2 />, title: "Daily Flash Report", link: "/hospitality/daily-flash-report", category: "Flash Reports" },
  { icon: <Activity />, title: "Shift End Summary", link: "/hospitality/shift-end-summary", category: "Flash Reports" },
  { icon: <Receipt />, title: "Check Out / Guest Billing", link: "/hospitality/front-office-billings", category: "Flash Reports" },
  { icon: <TrendingUp />, title: "Sales Performance and Occupancy", link: "/hospitality/sales-performance", category: "Flash Reports" },
  { icon: <Users />, title: "Reservation Report", link: "/hospitality/reservation-report", category: "Reservation & Occupancy Reports" },
  { icon: <User />, title: "Occupancy Report", link: "/hospitality/occupancy-report", category: "Reservation & Occupancy Reports" },
  { icon: <FileText  />, title: "Room Status Report", link: "/hospitality/room-status-report", category: "Reservation & Occupancy Reports" },
  { icon: <DollarSign />, title: "Daily Revenue Report", link: "/hospitality/daily-revenue-analysis", category: "Financial Reports" },
  { icon: <TrendingUp />, title: "Payment Mode Report", link: "/hospitality/payment-mode-report", category: "Financial Reports" },
  { icon: <Activity />, title: "Refund & Adjustments", link: "/reports/refunds", category: "Financial Reports" },
  { icon: <Folder />, title: "Tax Compliance Report", link: "/reports/tax-compliance", category: "Financial Reports" },
  { icon: <BarChart2 />, title: "Room Type Wise Sales Report", link: "/hospitality/daily-room-sold", category: "Financial Reports" },
  { icon: <Users />, title: "Guest Database", link: "/hospitality/guest-database", category: "Operational & Guest Insights" },
  { icon: <FileText />, title: "Daiy Room Sold Reports", link: "/hospitality/daily-room-sold", category: "Operational & Guest Insights" },
    { icon: <FileText />, title: "Date Wise Room Sold Reports", link: "/hospitality/daily-room-sold", category: "Operational & Guest Insights" },

  { icon: <PieChart />, title: "OTA Booking Volume Report", link: "/hospitality/ota-booking-volume", category: "OTA Reports" },
  { icon: <TrendingUp />, title: "OTA Revenue Performance", link: "/hospitality/ota-booking-revenue", category: "OTA Reports" },
];

const FrontOfficeReports = ({ searchQuery }) => {
  const navigate = useNavigate();

  const handleCardClick = (link) => {
    navigate(link);
  };

  const filteredReports = frontOfficeReports.map((category) => ({
    ...category,
    reports: category.reports.filter(
      (report) =>
        report.title.toLowerCase().includes(searchQuery) ||
        report.description.toLowerCase().includes(searchQuery)
    ),
  })).filter((category) => category.reports.length > 0);

  const filteredQuickLinks = quickLinks.filter(
    (link) =>
      link.title.toLowerCase().includes(searchQuery) ||
      link.category.toLowerCase().includes(searchQuery)
  );

  return (
    <Row gutter={[16, 16]}>
      <Col xs={24} md={16} lg={18}>
        {filteredReports.map((category, index) => (
          <div key={index} className="category-section mb-4">
            <h4
              className="category-title"
              style={{
                color: "#031b4e",
                fontSize: "20px",
                fontWeight: "400",
                marginBottom: "20px",
                display: "inline-block",
                borderBottom: `3px solid ${categoryColors[category.category]}`,
                paddingBottom: "5px",
              }}
            >
              {category.category}
            </h4>
            <Row gutter={[16, 16]}>
              {category.reports.map((report, idx) => (

<Col key={idx} xs={24} sm={12} md={12} lg={8}>
  <Card
    hoverable={report.link !== "/reports/ota-customer-insights"}
    style={{
      borderLeft: `4px solid ${categoryColors[category.category]}`,
      cursor: report.link === "/reports/ota-customer-insights" ? "not-allowed" : "pointer",
      borderRadius: "8px",
      padding: "12px",
      opacity: report.link === "/reports/ota-customer-insights" ? 0.5 : 1,
      pointerEvents: report.link === "/reports/ota-customer-insights" ? "none" : "auto", // disables interaction
    }}
    onClick={
      report.link === "/reports/ota-customer-insights"
        ? undefined
        : () => handleCardClick(report.link)
    }
  >
    <div
      style={{
        display: "flex",
        alignItems: "center",
        marginBottom: "8px",
      }}
    >
      <span
        style={{
          color: categoryColors[category.category],
          fontSize: "20px",
          marginRight: "8px",
        }}
      >
        {report.icon}
      </span>
      <h5
        style={{
          fontSize: "16px",
          fontWeight: "600",
          margin: 0,
        }}
      >
        {report.title}
      </h5>
    </div>
    <p style={{ fontSize: "14px", color: "#666", margin: 0 }}>
      {report.description}
    </p>
  </Card>
</Col>

              ))}
            </Row>
          </div>
        ))}
      </Col>
      <Col xs={24} md={8} lg={6}>
        <div
          style={{
            backgroundColor: "#f8f9fa",
            padding: "16px",
            borderRadius: "8px",
            borderLeft: "4px solid #007bff",
            minHeight: "100%",
            height: "fit-content",
            overflowY: "auto",
          }}
        >
          <h4
            style={{
              fontSize: "16px",
              fontWeight: "600",
              color: "#031b4e",
              marginBottom: "16px",
              display: "flex",
              alignItems: "center",
            }}
          >
            <Rocket size={18} style={{ marginRight: "8px", color: "#007bff" }} />
            Quick Access
          </h4>
<ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
  {filteredQuickLinks.map((link, idx) => (
    <li
      key={idx}
      onClick={() => handleCardClick(link.link)}
      style={{
        display: "flex",
        alignItems: "center",
        padding: "8px",
        marginBottom: "0px",
        borderBottom: idx !== filteredQuickLinks.length - 1 ? "1px solid #eee" : "none",
        cursor: "pointer",
        transition: "background-color 0.2s",
      }}
      className="quick-link-item"
    >
      <span
        style={{
          color: categoryColors[link.category],
          marginRight: "8px",
          fontSize: "16px",
        }}
      >
        {link.icon}
      </span>
      <span style={{ fontSize: "14px", color: "#333" }}>{link.title}</span>
    </li>
  ))}
</ul>

        </div>
      </Col>
    </Row>
  );
};

export default FrontOfficeReports;
